import React, { useState, useEffect, useRef } from 'react';
import { Search, X, MapPin, Navigation } from 'lucide-react';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng
} from 'use-places-autocomplete';

// Add mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      const windowWidth = window.innerWidth;
      // More robust check for mobile user agents
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isMobileWidth = windowWidth <= 768; // Common breakpoint for mobile
      const result = isMobileUA || isMobileWidth;
      
      console.log('[useIsMobile] checkIsMobile:', {
        userAgent: userAgent.substring(0, 80) + '...', // Log more of UA
        windowWidth,
        isMobileUA,
        isMobileWidth,
        checkResult: result
      });
      
      return result;
    };

    const updateMobile = () => {
      const newIsMobile = checkIsMobile();
      if (newIsMobile !== isMobile) {
        console.log('[useIsMobile] Mobile state changed:', { from: isMobile, to: newIsMobile });
        setIsMobile(newIsMobile);
      } else {
        // console.log('[useIsMobile] Mobile state unchanged:', newIsMobile); // Optional: log if state is same
      }
    };

    updateMobile();
    window.addEventListener('resize', updateMobile);
    return () => window.removeEventListener('resize', updateMobile);
  }, [isMobile]);

  console.log('[useIsMobile] Hook returning:', isMobile);
  return isMobile;
};

interface AddressSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAddress: (address: string, coordinates?: { lat: number; lng: number }) => void;
  title: string;
  placeholder: string;
  initialValue?: string;
  requestOptions?: any;
  isGoogleMapsLoaded: boolean;
}

const AddressSearchDialog: React.FC<AddressSearchDialogProps> = ({
  isOpen,
  onClose,
  onSelectAddress,
  title,
  placeholder,
  initialValue = '',
  requestOptions = {},
  isGoogleMapsLoaded
}) => {
  console.log('[AddressSearchDialog] Rendering with props:', { isOpen, title, placeholder, initialValue, requestOptions, isGoogleMapsLoaded });
  
  const isMobile = useIsMobile();
  console.log('[AddressSearchDialog] isMobile detected as:', isMobile);
  
  const [searchValue, setSearchValue] = useState(initialValue);
  console.log('[AddressSearchDialog] Initial searchValue:', searchValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const {
    ready,
    suggestions: { data: suggestions, loading, status },
    setValue: setAutocompleteValue,
    clearSuggestions,
    init // Added init function
  } = usePlacesAutocomplete({
    requestOptions,
    debounce: 300,
    cache: 86400,
    initOnMount: false,
    defaultValue: initialValue,
    callbackName: "googleMapsAddressSearchCallback",
  });
  
  console.log('[AddressSearchDialog] usePlacesAutocomplete state:', { ready, suggestionsLength: suggestions.length, loading, status, initialValue });

  useEffect(() => {
    console.log('[AddressSearchDialog] useEffect [status, ready, suggestions, isGoogleMapsLoaded, searchValue] triggered. Current values:', { status, ready, suggestionsCount: suggestions.length, isGoogleMapsLoadedProp: isGoogleMapsLoaded, currentSearchValue: searchValue });
    
    if (suggestions.length > 0) {
      console.log('[AddressSearchDialog] First suggestion available:', suggestions[0].description);
    }
    
    if (status === "ZERO_RESULTS") {
      console.log('[AddressSearchDialog] Google Places API status: ZERO_RESULTS for searchValue:', searchValue);
    } else if (status === "REQUEST_DENIED") {
      console.error('[AddressSearchDialog] Google Places API status: REQUEST_DENIED. Check API key and permissions.');
    } else if (status === "INVALID_REQUEST") {
      console.error('[AddressSearchDialog] Google Places API status: INVALID_REQUEST. Check request parameters.');
    } else if (status) {
      console.log('[AddressSearchDialog] Google Places API status:', status);
    }
  }, [status, ready, suggestions, isGoogleMapsLoaded, searchValue]);

  useEffect(() => {
    console.log('[AddressSearchDialog] useEffect [isGoogleMapsLoaded, ready, init] triggered. Current values:', { isGoogleMapsLoadedProp: isGoogleMapsLoaded, hookReady: ready });
    if (isGoogleMapsLoaded && !ready) {
      console.log('[AddressSearchDialog] Initializing usePlacesAutocomplete via init().');
      init();
    }
  }, [isGoogleMapsLoaded, ready, init]);

  useEffect(() => {
    console.log('[AddressSearchDialog] useEffect [isOpen, initialValue, setAutocompleteValue, clearSuggestions] triggered. Current values:', { isOpenProp: isOpen, initialValueProp: initialValue });
    if (isOpen) {
      console.log('[AddressSearchDialog] Dialog is open. Setting searchValue and autocompleteValue to initialValue:', initialValue);
      setSearchValue(initialValue);
      setAutocompleteValue(initialValue);
      setTimeout(() => {
        console.log('[AddressSearchDialog] Focusing inputRef.');
        inputRef.current?.focus();
      }, 100);
    } else {
      console.log('[AddressSearchDialog] Dialog is closed. Clearing suggestions.');
      clearSuggestions();
    }
  }, [isOpen, initialValue, setAutocompleteValue, clearSuggestions]);

  useEffect(() => {
    console.log('[AddressSearchDialog] useEffect [isOpen] for body scroll triggered. isOpen:', isOpen);
    // Prevent body scroll when dialog is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('[AddressSearchDialog] handleInputChange. New value:', value, 'Current ready state:', ready, 'isGoogleMapsLoaded:', isGoogleMapsLoaded);
    setSearchValue(value);
    setAutocompleteValue(value);

    if (!value) {
      console.log('[AddressSearchDialog] handleInputChange: Value is empty, clearing suggestions.');
      clearSuggestions();
    }
    console.log('[AddressSearchDialog] handleInputChange: After update. Suggestions count:', suggestions.length, 'Loading:', loading);
  };

  const handleSuggestionClick = async (suggestion: any) => {
    const address = suggestion.description;
    console.log('[AddressSearchDialog] handleSuggestionClick. Selected suggestion:', suggestion);
    setSearchValue(address);
    clearSuggestions();

    try {
      console.log('[AddressSearchDialog] handleSuggestionClick: Getting geocode for address:', address);
      const results = await getGeocode({ address });
      if (results.length > 0) {
        const coordinates = await getLatLng(results[0]);
        console.log('[AddressSearchDialog] handleSuggestionClick: Geocode success. Coordinates:', coordinates);
        onSelectAddress(address, coordinates);
      } else {
        console.log('[AddressSearchDialog] handleSuggestionClick: Geocode returned no results.');
        onSelectAddress(address);
      }
    } catch (error) {
      console.error('[AddressSearchDialog] handleSuggestionClick: Error getting coordinates:', error);
      onSelectAddress(address);
    }
    
    console.log('[AddressSearchDialog] handleSuggestionClick: Calling onClose.');
    onClose();
  };

  const handleConfirm = () => {
    console.log('[AddressSearchDialog] handleConfirm. Current searchValue:', searchValue);
    if (searchValue.trim()) {
      onSelectAddress(searchValue.trim());
      onClose();
    } else {
      console.log('[AddressSearchDialog] handleConfirm: searchValue is empty, not confirming.');
    }
  };

  const handleClear = () => {
    console.log('[AddressSearchDialog] handleClear called.');
    setSearchValue('');
    setAutocompleteValue('');
    clearSuggestions();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // console.log('[AddressSearchDialog] handleKeyDown. Key:', e.key); // Optional: too verbose
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        handleSuggestionClick(suggestions[0]);
      } else {
        handleConfirm();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };
  if (!isOpen) {
    console.log('[AddressSearchDialog] isOpen is false, component will return null. isMobile:', isMobile);
    return null;
  }

  console.log('[AddressSearchDialog] Component is rendering its JSX. isOpen:', isOpen, 'isMobile:', isMobile, 'searchValue:', searchValue, 'suggestions.length:', suggestions.length, 'loading:', loading);

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end md:items-center justify-center">
      <div
        ref={dialogRef}
        className="bg-background-paper w-full h-full md:h-auto md:max-h-[80vh] md:w-96 md:rounded-lg shadow-xl flex flex-col animate-slide-up md:animate-fade-in border border-border"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-foreground-muted" />
          </button>
        </div>        {/* Search Input */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-foreground-muted" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="form-input pl-10 pr-10 py-3 text-base"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck="false"
              autoFocus
            />
            {searchValue && (
              <button
                onClick={handleClear}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <X className="h-5 w-5 text-foreground-muted hover:text-foreground" />
              </button>
            )}
          </div>
        </div>        {/* Suggestions List */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-4 text-center text-foreground-muted">
              <div className="inline-flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                Searching addresses...
              </div>
            </div>
          )}
          {/* Suggestions List Logic with Logging */}
          {!loading && suggestions.length > 0 && searchValue.length > 1 && (
            console.log('[AddressSearchDialog] Rendering suggestions list. Count:', suggestions.length, 'SearchValue length:', searchValue.length),
            <div className="py-2">
              {suggestions.map((suggestion: any) => (
                <button
                  key={suggestion.place_id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-accent active:bg-accent transition-colors flex items-start space-x-3 border-b border-border last:border-b-0"
                >
                  <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground font-medium truncate">
                      {suggestion.structured_formatting?.main_text || suggestion.description}
                    </div>
                    {suggestion.structured_formatting?.secondary_text && (
                      <div className="text-foreground-muted text-sm truncate">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* No Results Found Logic with Logging */}
          {!loading && suggestions.length === 0 && searchValue.length > 1 && (
            console.log('[AddressSearchDialog] Rendering "No results found" for searchValue:', searchValue),
            <div className="p-4 text-center text-foreground-muted">
              No results found for "{searchValue}"
            </div>
          )}
          {/* Initial state or short search term (no explicit log needed here as it's the default if others aren't met) */}
        </div>        {/* Footer / Actions */}
        <div className="p-4 flex justify-end space-x-2 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm bg-primary rounded-lg text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Select Address
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressSearchDialog;
